import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowLeft, ArrowRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import Label from "@/components/Label";
import BookingEmbed from "@/components/BookingEmbed";
import { SERVICES, ALL_FAQS } from "@/data/content";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);

  if (!service) return <Navigate to="/services" replace />;

  const faqs = service.faqs
    .map((id) => ALL_FAQS.find((f) => f.id === id))
    .filter(Boolean);
  const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <div data-testid="service-detail-page">
      <Seo
        title={service.title}
        path={`/services/${service.slug}`}
        description={service.intro}
        image={service.image}
        type="article"
      />
      {/* Hero */}
      <section className="border-b border-black/10 pt-8 md:pt-14">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-0 lg:grid-cols-2">
          <div className="flex flex-col justify-center px-5 md:px-10 py-10 md:py-16">
            <Link
              to="/services"
              className="mb-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#888] hover:text-black"
            >
              <ArrowLeft size={14} /> All services
            </Link>
            <div className="mb-4"><Label>Service</Label></div>
            <h1 className="font-serif-display font-normal tracking-tight leading-[0.98] text-5xl md:text-6xl overflow-hidden">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
              >
                {service.title}
              </motion.span>
            </h1>
            <p className="mt-6 max-w-lg text-base md:text-lg leading-relaxed text-[#3d3d3d]">
              {service.intro}
            </p>
            <ul className="mt-8 space-y-3">
              {service.highlights.map((h) => (
                <li key={h} className="flex items-center gap-3 text-sm text-[#333]">
                  <span className="flex h-5 w-5 items-center justify-center border border-black/20">
                    <Check size={12} />
                  </span>
                  {h}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative min-h-[320px] overflow-hidden rounded-2xl lg:min-h-[560px]">
            <img
              src={service.image}
              alt={service.title}
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* FAQ + Booking */}
      <section className="mx-auto max-w-[1400px] px-5 md:px-10 py-20 md:py-28">
        <div className="grid gap-14 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7">
            <Reveal>
              <Label className="mb-4">Good To Know</Label>
              <h2 className="mt-1 font-serif-display text-4xl md:text-5xl tracking-tight">
                Frequently Asked
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Accordion type="single" collapsible className="mt-8" data-testid="service-faq">
                {faqs.map((f) => (
                  <AccordionItem
                    key={f.id}
                    value={f.id}
                    className="border-b border-black/12"
                  >
                    <AccordionTrigger className="py-5 text-left font-serif-display text-xl md:text-2xl tracking-tight hover:no-underline">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="pb-6 text-base leading-relaxed text-[#3d3d3d]">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <BookingEmbed service={service.title} />
            </div>
          </div>
        </div>
      </section>

      {/* Other services */}
      <section className="border-t border-black/10 bg-[#f5f5f4]">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-16 md:py-20">
          <h3 className="mb-10 font-serif-display text-3xl md:text-4xl tracking-tight">
            Explore more
          </h3>
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-3">
            {others.map((o) => (
              <Link
                key={o.slug}
                to={`/services/${o.slug}`}
                className="group flex items-center justify-between border-t border-black/15 pt-5"
              >
                <span className="font-serif-display text-2xl tracking-tight">{o.title}</span>
                <ArrowRight
                  size={18}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
